import type { ReactNode } from "react";
import { useIsMobile } from "../../lib/useMediaQuery";

interface Props {
  /* The small soft label above the title — e.g., "Operator console" */
  eyebrow?: ReactNode;
  /* Main page title — rendered in Source Serif display font */
  title: ReactNode;
  /* Secondary one-liner under the title */
  subtitle?: ReactNode;
  /* Right-aligned action buttons */
  actions?: ReactNode;
  /* Optional inline metadata row (e.g., "47 total · 3 active") */
  meta?: ReactNode;
}

/**
 * The canonical page header. Boardroom aesthetic: refined sentence-case
 * eyebrow, Source Serif display title, soft subtitle, right-aligned
 * actions. No mono caps, no diamond glyphs.
 */
export function PageHeader({ eyebrow, title, subtitle, actions, meta }: Props) {
  const isMobile = useIsMobile();
  return (
    <header
      style={{
        display: "grid",
        // Phone: stack the title block and actions into one column so the
        // actions (filter strips, "New …" buttons, transition controls) drop
        // onto their own full-width row instead of crushing the title.
        gridTemplateColumns: isMobile ? "1fr" : "1fr auto",
        gap: isMobile ? 14 : 16,
        alignItems: isMobile ? "stretch" : "flex-end",
        paddingBottom: isMobile ? 18 : 26,
        borderBottom: "1px solid var(--border-subtle)",
        marginBottom: isMobile ? 20 : 28,
      }}
    >
      <div style={{ minWidth: 0 }}>
        {eyebrow ? (
          <div
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: 12.5,
              fontWeight: 500,
              letterSpacing: "0.02em",
              color: "var(--text-muted)",
              marginBottom: 10,
            }}
          >
            {eyebrow}
          </div>
        ) : null}
        <h1
          style={{
            margin: 0,
            fontFamily: "var(--font-display)",
            fontSize: isMobile ? 26 : 34,
            fontWeight: 500,
            letterSpacing: "-0.022em",
            color: "var(--text-primary)",
            lineHeight: 1.1,
          }}
        >
          {title}
        </h1>
        {subtitle ? (
          <p
            style={{
              margin: "10px 0 0",
              fontSize: 14,
              color: "var(--text-secondary)",
              lineHeight: 1.55,
              maxWidth: 680,
              letterSpacing: "-0.005em",
            }}
          >
            {subtitle}
          </p>
        ) : null}
        {meta ? (
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: "var(--text-muted)",
            }}
          >
            {meta}
          </div>
        ) : null}
      </div>
      {actions ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            // Wrap multi-button action rows on narrow screens (filter strips,
            // transition controls) instead of overflowing the header.
            flexWrap: "wrap",
            justifyContent: isMobile ? "flex-start" : "flex-end",
          }}
        >
          {actions}
        </div>
      ) : null}
    </header>
  );
}
